import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "@store/index";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";
import {
  BalanceItemResponse,
  DdtProps,
  DeliveryCreateDetail,
  DeliveryCreateGeneral,
  DeliveryCreateInfo,
  DeliveryCreateWHSInfo,
  DeliveryDetailItem,
  DeliveryDetails,
  DeliveryDetailsResponse,
  DeliveryGeneral,
  DeliveryItemResponse,
  DeliveryReadinessResponse,
  DeliveryWHSDetailItem,
  OrderItemResponse,
  PassivePiceItem,
} from "./types";
import {
  cancelItem,
  cancelItemById,
  cancelWHSItemById,
  createDeliveryInfoApi,
  createDeliveryOrderDetailsApi,
  createDeliveryOrderGeneralApi,
  createDeliveryWHSInfoApi,
  exportDeliveryOrderApi,
  exportLoadingDdtApi,
  getBalanceListApi,
  getDeliveryListApi,
  getDeliveryOrderApi,
  getDeliveryOrderDetailsApi,
  getDeliveryReadinessApi,
  getOrderListApi,
  getPassivePriceApi,
  getWhsListApi,
  removeOrderInfoApi,
  updateDeliveryInfoApi,
  updateDeliveryOrderDetailsApi,
  updateDeliveryOrderGeneralApi,
  updateDeliveryWHSInfoApi,
} from "./api";
import dayjs from "@helpers/dayjs";

interface ListState<T> {
  items: T[] | null;
  total: number;
  page: number;
  size: number;
  loading: boolean;
}

interface Payload<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

interface ReadinessItem extends DeliveryReadinessResponse {
  loading: boolean;
}

type ReadinessState = Record<string | number, ReadinessItem>;

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type DeliveryState = {
  deliveryView: ViewState<DeliveryItemResponse[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  orderList: ViewState<OrderItemResponse[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  balance: ViewState<BalanceItemResponse[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  whsList: ViewState<DeliveryWHSDetailItem[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  deliveryDetails: ViewState<DeliveryGeneral | null>;
  activeOrders: { id: number; ref: string } | null;
  exporting: {
    loading: boolean;
    success: boolean | null;
    error: string | null;
    id: number | null;
  };
  passivePrice: ViewState<PassivePiceItem[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  readiness: ReadinessState;
};

export type DeliveryKey =
  | "deliveryView"
  | "deliveryDetails"
  | "orderList"
  | "whsList"
  | "passivePrice"
  | "balance";

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const initialState: DeliveryState = {
  deliveryView: {
    ...getInitialState<DeliveryItemResponse[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  orderList: {
    ...getInitialState<OrderItemResponse[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  balance: {
    ...getInitialState<BalanceItemResponse[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  whsList: {
    ...getInitialState<DeliveryWHSDetailItem[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  passivePrice: {
    ...getInitialState<PassivePiceItem[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  deliveryDetails: getInitialState<DeliveryGeneral | null>(null),
  activeOrders: null,
  exporting: {
    id: null,
    loading: false,
    success: null,
    error: null,
  },
  readiness: {},
};

function createListSuccessReducer<T>(key: keyof DeliveryState) {
  return (state: DeliveryState, action: PayloadAction<Payload<T>>) => {
    const list = state[key] as ListState<T>;

    list.items = action.payload.items;
    list.total = action.payload.total;
    list.page = action.payload.page;
    list.size = action.payload.size;
    list.loading = false;
  };
}

const deliverySlice = createSlice({
  name: "delivery",
  initialState,
  reducers: {
    getStart(state: DeliveryState, action: PayloadAction<DeliveryKey>) {
      state[action.payload].loading = true;
      state[action.payload].error = null;
    },
    getSuccess(
      state: DeliveryState,
      action: PayloadAction<{ key: DeliveryKey; items: any }>,
    ) {
      const { key, items } = action.payload;
      state[key].items = items;
      state[key].loading = false;
      state[key].error = null;
    },
    getOrderListSuccess:
      createListSuccessReducer<OrderItemResponse>("orderList"),
    getDeliveryListSuccess:
      createListSuccessReducer<DeliveryItemResponse>("deliveryView"),
    getBalanceSuccess: createListSuccessReducer<BalanceItemResponse>("balance"),
    getWhsListSuccess:
      createListSuccessReducer<DeliveryWHSDetailItem>("whsList"),
    createSuccess(
      state: DeliveryState,
      action: PayloadAction<{ key: DeliveryKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
      state[key].error = null;
    },
    getFailure(
      state: DeliveryState,
      action: PayloadAction<{ key: DeliveryKey; error: string }>,
    ) {
      const { key, error } = action.payload;
      state[key].loading = false;
      state[key].error = error;
      state.exporting.loading = false;
    },
    getSilentFailure(state: DeliveryState, action: PayloadAction<DeliveryKey>) {
      const key = action.payload;
      state[key].loading = false;
      state[key].error = null;
    },
    cleanData(state: DeliveryState, action: PayloadAction<DeliveryKey>) {
      state[action.payload].items = null;
      state[action.payload].success = null;
      state[action.payload].error = null;
    },
    hideWarning(state: DeliveryState, action: PayloadAction<DeliveryKey>) {
      const key = action.payload;
      state[key].error = null;
      state[key].success = null;
      state.exporting.error = null;
      state.exporting.success = null;
    },
    setActiveOrder(
      state: DeliveryState,
      action: PayloadAction<{ id: number; ref: string } | null>,
    ) {
      state.activeOrders = action.payload;
    },
    setDeliveryOrder(
      state: DeliveryState,
      action: PayloadAction<{ data: DeliveryGeneral; id?: number }>,
    ) {
      const { data } = action.payload;
      const existingDelivery = state.deliveryDetails.items?.delivery ?? [];

      state.deliveryDetails.items = {
        ...data,
        delivery: existingDelivery,
      };
    },
    setDeliveryOrderDetails(
      state: DeliveryState,
      action: PayloadAction<{
        data: DeliveryDetails | DeliveryDetailsResponse;
        id: number;
      }>,
    ) {
      const { data, id } = action.payload;
      const deliveryItems = "items" in data ? data.items : [data];
      const item = state.deliveryDetails.items;

      if (!item || item.id !== id) return;

      item.delivery = [...(item.delivery || []), ...deliveryItems];

      state.deliveryDetails.loading = false;
    },
    setDeliveryInfo(
      state: DeliveryState,
      action: PayloadAction<{
        data: DeliveryDetailItem;
        id: number;
      }>,
    ) {
      const { data, id } = action.payload;

      const deliveries = state.deliveryDetails.items?.delivery;
      if (!deliveries) return;

      const delivery = deliveries.find((item) => item.id === id);

      if (!delivery) return;

      if (!Array.isArray(delivery.delivery_infos)) {
        delivery.delivery_infos = [];
      }

      delivery.delivery_infos.push(data);
    },
    setDeliveryWHSInfo(
      state: DeliveryState,
      action: PayloadAction<{
        data: DeliveryWHSDetailItem;
        id: number;
      }>,
    ) {
      const { data, id } = action.payload;

      const deliveries = state.deliveryDetails.items?.delivery;
      if (!deliveries) return;

      const delivery = deliveries.find((item) => item.id === id);

      if (!delivery) return;

      if (!Array.isArray(delivery.delivery_whs)) {
        delivery.delivery_whs = [];
      }

      delivery.delivery_whs.push(data);
    },
    updateDeliveryDetails(
      state: DeliveryState,
      action: PayloadAction<{ id: number; data: DeliveryDetailItem }>,
    ) {
      const { data, id } = action.payload;
      const item = state.deliveryDetails.items;

      if (!item || !Array.isArray(item.delivery)) return;

      const index = item.delivery.findIndex((el) => el.id === id);
      if (index === -1) return;

      item.delivery[index] = { ...item.delivery[index], ...data };
    },
    removeDeliveryDetails(state: DeliveryState, action: PayloadAction<number>) {
      const item = state.deliveryDetails.items;

      if (!item || !Array.isArray(item.delivery)) return;

      item.delivery = item.delivery.filter((el) => el.id !== action.payload);
    },
    startExport(state: DeliveryState, action: PayloadAction<number>) {
      state.exporting = {
        id: action.payload,
        success: null,
        loading: true,
        error: null,
      };
    },
    successExport(state: DeliveryState) {
      state.exporting.loading = false;
      state.exporting.success = true;
      state.exporting.error = null;
      state.exporting.id = null;
    },
    setDeliveryInfoUpdated(
      state: DeliveryState,
      action: PayloadAction<{
        id: number;
        data: DeliveryDetailItem;
      }>,
    ) {
      const { id, data } = action.payload;
      const deliveries = state.deliveryDetails.items?.delivery;

      if (!deliveries) return;

      for (const delivery of deliveries) {
        if (!delivery.delivery_infos) continue;

        const exists = delivery.delivery_infos.some((info) => info.id === id);
        if (exists) {
          delivery.delivery_infos = delivery.delivery_infos.map((info) =>
            info.id === id ? { ...info, ...data } : info,
          );
          break;
        }
      }
    },
    setDeliveryWHSInfoUpdated(
      state: DeliveryState,
      action: PayloadAction<{
        id: number;
        data: DeliveryWHSDetailItem;
      }>,
    ) {
      const { id, data } = action.payload;
      const deliveries = state.deliveryDetails.items?.delivery;

      if (!deliveries) return;

      for (const delivery of deliveries) {
        if (!delivery.delivery_whs) continue;

        const exists = delivery.delivery_whs.some((info) => info.id === id);
        if (exists) {
          delivery.delivery_whs = delivery.delivery_whs.map((info) =>
            info.id === id ? { ...info, ...data } : info,
          );
          break;
        }
      }
    },
    updatePartialDeliveryLabels(
      state: DeliveryState,
      action: PayloadAction<{ truckId: number }>,
    ) {
      const { truckId } = action.payload;
      const deliveries = state.deliveryDetails.items?.delivery;

      if (!deliveries) return;

      const truck = deliveries.find((d) => d.id === truckId);
      if (!truck || !truck.partial_delivery || !truck.delivery_infos?.length)
        return;

      truck.delivery_infos[0] = {
        ...truck.delivery_infos[0],
        label: `Delivery | A`,
      };
    },
    updateTruckInfos(
      state: DeliveryState,
      action: PayloadAction<{ id: number; data: DeliveryDetailItem[] }>,
    ) {
      const { id, data } = action.payload;
      const truck = state.deliveryDetails.items?.delivery.find(
        (item) => item.id === id,
      );
      if (truck) {
        truck.delivery_infos = data;
      }
    },
    setDuplicateTruck(state: DeliveryState, action: PayloadAction<number>) {
      const id = action.payload;
      const order = state.deliveryDetails.items;
      if (!order) return;
      const truck = order?.delivery.find((item) => item.id === id);

      if (!truck) return;

      const now = dayjs();
      const isWHS = !!truck.delivery_whs?.length;

      const draftDeliveries = truck.delivery_infos?.map((d) => ({
        ...d,
        id: `draft_${d.id}`,
        commodity_ids: d.commodities?.map((c) => c.id) || undefined,
        from_city_id: d.from_city?.id || undefined,
        to_city_id: d.to_city?.id || undefined,
        loading_warehouse_id: d.loading_warehouse?.id || undefined,
        discharge_address_id: d.discharge_address?.id || undefined,
        transport_payer_id: d.transport_payer?.id || undefined,
        depositor_id: d.depositor?.id || undefined,
        consignee_id: d.consignee?.id || undefined,
        opening_hours: d.loading_warehouse?.opening_hours || undefined,
        special_instruction:
          d.discharge_address?.special_instruction || undefined,
        discharging_time: d.discharge_address?.discharging_time || undefined,
        transport_contactor: order.transport_contactor || undefined,
        loading_date: dayjs(d.loading_date).isBefore(now, "day")
          ? undefined
          : d.loading_date,
        discharge_date: dayjs(d.discharge_date).isBefore(now, "day")
          ? undefined
          : d.discharge_date,
      }));

      const draftDeliveriesWHS = truck.delivery_whs?.map((d) => ({
        ...d,
        id: `draft_${d.id}`,
        commodity_ids: d.commodities?.map((c) => c.id) || undefined,
        depositor_id: d.depositor?.id || undefined,
        consignee_id: d.consignee?.id || null,
        consignee_type: d.consignee ? "CUSTOMER" : "SEAWAY",
        loading_date: d.loading_date,
        discharge_date: d.discharge_date,
      }));

      const copy_truck = {
        ...truck,
        id: undefined,
        truck_kind_id: truck.truck_kind?.id || null,
        delivery_infos: isWHS ? [] : draftDeliveries || [],
        delivery_whs: isWHS ? draftDeliveriesWHS || [] : [],
      } as any;

      order.delivery = [...(order.delivery || []), copy_truck];
    },
    updateDuplicateTruck(
      state: DeliveryState,
      action: PayloadAction<DeliveryDetails>,
    ) {
      const data = action.payload;
      const order = state.deliveryDetails.items;
      if (!order) return;
      const copy_truck = order?.delivery.find((item) => item.id === undefined);
      if (!copy_truck) return;
      const infos = copy_truck.delivery_infos || [];
      const whsInfos = copy_truck.delivery_whs || [];
      Object.assign(copy_truck, data);
      copy_truck.delivery_infos = infos;
      copy_truck.delivery_whs = whsInfos;
    },
    removeDuplicateDelivery(
      state: DeliveryState,
      action: PayloadAction<{
        id: number | string;
        deliveryId: number | string;
      }>,
    ) {
      const { id, deliveryId } = action.payload;
      const order = state.deliveryDetails.items;
      if (!order) return;
      const truck = order.delivery?.find((item) => item.id === id);
      if (!truck) return;
      truck.delivery_infos = truck.delivery_infos?.filter(
        (info) => info.id !== deliveryId,
      );
      truck.delivery_whs = truck.delivery_whs?.filter(
        (info) => info.id !== deliveryId,
      );
    },
    updateDuplicateDelivery(
      state: DeliveryState,
      action: PayloadAction<{
        id: number | string;
        deliveryId: number | string;
        data: DeliveryDetailItem | DeliveryWHSDetailItem;
      }>,
    ) {
      const { id, deliveryId, data } = action.payload;
      const order = state.deliveryDetails.items;
      if (!order) return;
      const truck = order.delivery?.find((item) => item.id === id);
      if (!truck) return;
      truck.delivery_infos = truck.delivery_infos?.map((info) =>
        info.id === deliveryId ? { ...info, ...data } : info,
      );
      truck.delivery_whs = truck.delivery_whs?.map((info) =>
        info.id === deliveryId ? { ...info, ...data } : info,
      ) as any;
    },
    cleanCopyTruck(state: DeliveryState) {
      const order = state.deliveryDetails.items;
      if (!order) return;
      order.delivery = order.delivery?.filter((item) => item.id !== undefined);
    },
    setReadiness(
      state: DeliveryState,
      action: PayloadAction<{
        id: number | string;
        data: DeliveryReadinessResponse;
      }>,
    ) {
      const { id, data } = action.payload;
      if (!state.readiness[id]) {
        state.readiness[id] = {} as any;
      }
      state.readiness[id].ready = data?.ready;
      state.readiness[id].missed_columns = data?.missed_columns;
      state.readiness[id].loading = false;
    },
    setReadinessLoading(
      state: DeliveryState,
      action: PayloadAction<number | string>,
    ) {
      const id = action.payload;
      if (!state.readiness[id]) {
        state.readiness[id] = {
          loading: true,
        } as any;
      } else {
        state.readiness[id].loading = true;
        state.readiness[id].ready = undefined;
      }
    },
    cleanReadiness(
      state: DeliveryState,
      action: PayloadAction<number | string>,
    ) {
      const id = action.payload;
      if (!state.readiness[id]) {
        state.readiness[id] = {} as any;
      }
      state.readiness[id].ready = undefined;
      state.readiness[id].missed_columns = undefined;
      state.readiness[id].loading = false;
    },
  },
});

export const selectReadinessById = (state: RootState, id: string | number) =>
  state.viewShipping.delivery.readiness[id];

export const selectReadinessByIdNew = (state: RootState) =>
  state.viewShipping.delivery.readiness["new"];

export const {
  getStart,
  cleanData,
  getSuccess,
  getFailure,
  startExport,
  hideWarning,
  setReadiness,
  createSuccess,
  successExport,
  cleanReadiness,
  setActiveOrder,
  setDeliveryInfo,
  setDeliveryWHSInfo,
  updatePartialDeliveryLabels,
  updateTruckInfos,
  getSilentFailure,
  setDeliveryOrder,
  getBalanceSuccess,
  setReadinessLoading,
  getOrderListSuccess,
  removeDeliveryDetails,
  updateDeliveryDetails,
  getDeliveryListSuccess,
  setDeliveryOrderDetails,
  setDeliveryInfoUpdated,
  setDeliveryWHSInfoUpdated,
  cleanCopyTruck,
  setDuplicateTruck,
  updateDuplicateTruck,
  removeDuplicateDelivery,
  updateDuplicateDelivery,
  getWhsListSuccess,
} = deliverySlice.actions;

export const { actions, reducer } = deliverySlice;
export default deliverySlice.reducer;

export const fetchVolumeBalance =
  (
    id: number,
    params: { page: number; size: number; order_by?: string },
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("balance"));
      const data = await getBalanceListApi(id, params);
      dispatch(getBalanceSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "balance", error }));
    }
  };

export const fetchDelivery =
  (id: number, params: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("deliveryView"));
      const data = await getDeliveryListApi(id, params);
      dispatch(getDeliveryListSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryView", error }));
    }
  };

export const fetchWhsList =
  (id: number, params: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("whsList"));
      const data = await getWhsListApi(id, params);
      dispatch(getWhsListSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "whsList", error }));
    }
  };

export const fetchOrderList =
  (id: number, params?: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("orderList"));
      const data = await getOrderListApi(id, params);
      dispatch(getOrderListSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "orderList", error }));
    }
  };

export const fetchDeliveryDetails =
  (id: number, isWHS: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("deliveryDetails"));
      const data = await getDeliveryOrderApi(id);
      dispatch(fetchOrderDetails(data.id, isWHS));
      dispatch(setDeliveryOrder({ data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const fetchOrderDetails =
  (id: number, isWHS: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("deliveryDetails"));
      const data = await getDeliveryOrderDetailsApi(id, isWHS);
      dispatch(setDeliveryOrderDetails({ data, id }));
    } catch {
      dispatch(getSilentFailure("deliveryDetails"));
    }
  };

export const createDeliveryOrderGeneral =
  ({
    id,
    params,
    navigate,
    route,
  }: {
    id: number;
    params: Partial<DeliveryCreateGeneral>;
    navigate: (base: string) => void;
    route: string;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createDeliveryOrderGeneralApi(id, params);
      const url = data.warehouse_shifting_id
        ? `${route}/${data.id}?warehouse_shifting_id=${data.warehouse_shifting_id}`
        : `${route}/${data.id}`;
      navigate(url);
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery order created successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const createDeliveryOrderDetails =
  ({
    id,
    params,
    draft,
    isWHS,
  }: {
    id: number;
    params: Partial<DeliveryCreateDetail & { id: undefined | number | string }>;
    draft?: boolean;
    isWHS?: boolean;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createDeliveryOrderDetailsApi(id, params, isWHS);
      if (draft) {
        dispatch(updateDuplicateTruck(data));
      } else {
        dispatch(setDeliveryOrderDetails({ data, id }));
      }
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information added successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const createDeliveryInfo =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<DeliveryCreateInfo & { id: string | undefined | number }>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createDeliveryInfoApi(id, params);

      if (String(params.id).includes("draft_")) {
        dispatch(
          updateDuplicateDelivery({
            id,
            deliveryId: params.id as string,
            data,
          }),
        );
      } else {
        const dataWithStatus = {
          ...data,
          status: data.status ?? "PENDING",
          loading_status: (data as any).loading_status ?? "PENDING",
        };
        dispatch(setDeliveryInfo({ data: dataWithStatus, id }));
      }
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information added successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const createDeliveryWHSInfo =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<
      DeliveryCreateWHSInfo & { id: string | undefined | number }
    >;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createDeliveryWHSInfoApi(id, params);

      if (String(params.id).includes("draft_")) {
        dispatch(
          updateDuplicateDelivery({
            id,
            deliveryId: params.id as string,
            data,
          }),
        );
      } else {
        const dataWithStatus = {
          ...data,
          status: data.status ?? "PENDING",
          loading_status: (data as any).loading_status ?? "PENDING",
        };
        dispatch(setDeliveryWHSInfo({ data: dataWithStatus, id }));
      }
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information added successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const updateDeliveryOrderDetails =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<DeliveryCreateDetail>;
  }): AppThunk =>
  async (dispatch, getState) => {
    try {
      const data = await updateDeliveryOrderDetailsApi(id, params);
      dispatch(updateDeliveryDetails({ data, id }));

      if ("partial_delivery" in params) {
        const state = getState();
        const updatedTruck =
          state.viewShipping.delivery.deliveryDetails.items?.delivery?.find(
            (truck) => truck.id === id,
          );

        if (updatedTruck?.partial_delivery) {
          dispatch(updatePartialDeliveryLabels({ truckId: id }));
        }
      }

      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const updateDeliveryInfo =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<DeliveryCreateInfo>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateDeliveryInfoApi(id, params);
      dispatch(setDeliveryInfoUpdated({ id, data }));
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const updateDeliveryWHSInfo =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<DeliveryCreateWHSInfo>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateDeliveryWHSInfoApi(id, params);
      dispatch(setDeliveryWHSInfoUpdated({ id, data }));
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const updateDeliveryOrderGeneral =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<DeliveryCreateGeneral>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateDeliveryOrderGeneralApi(id, params);
      dispatch(setDeliveryOrder({ data }));
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery order updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const deleteDeliveryOrderDetails =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeOrderInfoApi(id);
      dispatch(removeDeliveryDetails(id));
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery information removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const exportLoadingDdt =
  (id: number, params: DdtProps, isWhs?: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport(id));
      const { file, filename } = await exportLoadingDdtApi(id, params, isWhs);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(
        getFailure({
          key: "deliveryDetails",
          error: "Downloading failed. Try again later.",
        }),
      );
    }
  };

export const exportDeliveryOrder =
  (id: number, isWhs?: boolean, isWhsList?: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport(id));
      const { file, filename } = await exportDeliveryOrderApi(id, isWhs);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      if (isWhsList)
        dispatch(
          getFailure({
            key: "whsList",
            error: "Downloading failed. Try again later.",
          }),
        );
      else
        dispatch(
          getFailure({
            key: "deliveryDetails",
            error: "Downloading failed. Try again later.",
          }),
        );
    }
  };

export const cancelDeliveryItem =
  (
    id: number,
    sid?: number,
    params?: Record<string, any>,
    deliveryId?: number,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const data = await cancelItem(id);
      if (params && sid) dispatch(fetchOrderList(sid, params));
      else if (deliveryId) {
        dispatch(
          setDeliveryInfoUpdated({
            id: deliveryId,
            data: { ...data, loading_status: "CANCELED" },
          }),
        );
        dispatch(
          createSuccess({
            key: "deliveryDetails",
            success: "Delivery item cancelled successfully",
          }),
        );
      }
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "orderList", error }));
    }
  };

export const cancelDeliveryItemById =
  (deliveryId: number): AppThunk =>
  async (dispatch) => {
    try {
      const data = await cancelItemById(deliveryId);
      dispatch(
        setDeliveryInfoUpdated({
          id: deliveryId,
          data: {
            ...(data as DeliveryDetailItem),
            loading_status: "CANCELED",
          },
        }),
      );
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery item cancelled successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "orderList", error }));
    }
  };

export const cancelDeliveryWHSItemById =
  (deliveryId: number): AppThunk =>
  async (dispatch) => {
    try {
      const data = await cancelWHSItemById(deliveryId);
      dispatch(
        setDeliveryWHSInfoUpdated({
          id: deliveryId,
          data: {
            ...(data as DeliveryWHSDetailItem),
            loading_status: "CANCELED",
          },
        }),
      );
      dispatch(
        createSuccess({
          key: "deliveryDetails",
          success: "Delivery item cancelled successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "orderList", error }));
    }
  };

export const cancelWhsDeliveryItem =
  (id: number, sid: number, params?: Record<string, any>): AppThunk =>
  async (dispatch) => {
    try {
      await cancelWHSItemById(id);
      dispatch(fetchWhsList(sid, params));
      dispatch(
        createSuccess({
          key: "whsList",
          success: "Delivery item cancelled successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "whsList", error }));
    }
  };

export const getDeliveryReadiness =
  (id: number | string, sid: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setReadinessLoading(id));
      const data = await getDeliveryReadinessApi(sid);
      dispatch(setReadiness({ id, data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };

export const getPassivePrice =
  (url: string): AppThunk =>
  async (dispatch) => {
    try {
      const data = await getPassivePriceApi(url);
      dispatch(getSuccess({ key: "passivePrice", items: data.items }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "deliveryDetails", error }));
    }
  };
